import {useRef, useLayoutEffect} from 'react';
import {isNil} from 'lodash-es';
import {useForceUpdate} from './useForceUpdate';
import {useConstant} from '../../shared-ui/hooks/useConstant';

type LoadValueCallback<TKey, TValue> = (key: TKey) => Promise<TValue>;

export function useCache<TKey, TValue>(loadValueCallback: LoadValueCallback<TKey, TValue>): {
  getValue: (key: TKey) => TValue | undefined,
  setLocalValue: (key: TKey, value: TValue | undefined) => void,
  waitForValue: (key: TKey) => Promise<TValue>,
  clearCache: (key?: TKey) => void
} {

  // Values loaded from the server
  const remoteValues = useConstant<Map<string, TValue>>(new Map<string, TValue>());

  // Temporary values populated by the client while waiting for the cache to be populated by the server
  const localValues = useConstant<Map<string, TValue>>(new Map<string, TValue>());

  // Reference to the latest version of loadValueCallback
  const loadValueCallbackRef = useRef<LoadValueCallback<TKey, TValue>>(loadValueCallback);

  // Used to re-render components downstream of the cache
  const forceUpdate = useForceUpdate();

  // Keep a reference to the latest loadValueCallback
  useLayoutEffect(
    () => {
      loadValueCallbackRef.current = loadValueCallback;
    },
    [loadValueCallback]
  );

  // Convert the key to a string for lookup
  const mapToLookup = (key: TKey): string => {
    // We use JSON.stringify for a fast but stable lookup that does a deep comparison of the key
    return JSON.stringify(key);
  };

  // Get the value for the key. If the value isn't already in the cache, loadValueCallback() is called to load it
  // asynchronously. Returns immediately with either the cached value, or undefined if the value is not yet available.
  const getValue = (key: TKey): TValue | undefined => {
    if (isNil(key)) {
      return undefined;
    }

    const lookup = mapToLookup(key);

    // If a local value is available, return it while we wait for the server to respond
    if (localValues.has(lookup)) {
      return localValues.get(lookup);
    }

    // If the remote value is already in the cache, return it...
    if (remoteValues.has(lookup)) {
      return remoteValues.get(lookup);
    }

    // ...otherwise, load it from the server and cache it for future use
    loadValueCallbackRef.current(key).then(value => {
      remoteValues.set(lookup, value);
      localValues.delete(lookup);
      forceUpdate();
    });

    // Meanwhile, return undefined to indicate that the value is not yet available
    remoteValues.set(lookup, undefined);
    return undefined;
  };

  // Populates a temporary value for the key, used while waiting for the server to respond
  const setLocalValue = (key: TKey, value: TValue | undefined) => {
    if (isNil(key)) {
      return;
    }

    localValues.set(mapToLookup(key), value);
    // NOTE: don't notify downstream components so that re-rendering doesn't interfere with inputs
  };

  // Awaits a value for the key, loading it from the server if necessary
  const waitForValue = async (key: TKey): Promise<TValue | undefined> => {
    // Nil keys are not supported
    if (isNil(key)) {
      return undefined;
    }

    const lookup = mapToLookup(key);

    // If the value is already in the cache, return it immediately...
    if (remoteValues.has(lookup)) {
      return remoteValues.get(lookup);
    }

    // ...otherwise, load it from the server and cache it for future use...
    const remoteValue = await loadValueCallbackRef.current(key);
    remoteValues.set(lookup, remoteValue);
    localValues.delete(lookup);

    // ...notify any downstream components that the cache has been updated...
    forceUpdate();

    // ...and return the value
    return remoteValue;
  };

  // Clear the cache for either a specific key or all keys
  const clearCache = (key?: TKey): void => {
    if (isNil(key)) {
      // Clear all values
      localValues.clear();
      remoteValues.clear();
    } else {
      // Clear a specific value
      const lookup = mapToLookup(key);
      localValues.delete(lookup);
      remoteValues.delete(lookup);
    }
    forceUpdate();
  };

  return {
    getValue: getValue,
    setLocalValue: setLocalValue,
    waitForValue: waitForValue,
    clearCache: clearCache
  };
}